import { useCallback, useEffect, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import {
  useAccessToken,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { getPublicEnvVars } from '@b-stock/fe-core/bstock-next/helpers'
import { getNotificationReadReceiptsClient } from '@b-stock/fe-core/bstock-next/queries'
import {
  buildAxiosRequestConfig,
  usePusher,
} from '@b-stock/fe-core/bstock-react'
// pay-ui's barrel also exports heavier payment-only components (payment
// method modals, Balance SDK loaders) alongside usePollUntil, and isn't
// exported from a lighter fe-core entry point - this matches the existing
// `use3MPAuctionUpdates.ts` import pattern, but worth re-checking with a
// bundle analyzer if header bundle size ever becomes a measured problem.
import { usePollUntil } from '@b-stock/fe-core/pay-ui'
import type { NotificationReadReceiptItemDto } from '@b-stock/notification-api-client'

import { getNotificationListKey } from '@/queries/notificationListQuery'
import { getNotificationUnreadCountKey } from '@/queries/notificationUnreadCountQuery'

const NOTIFICATION_CREATED_EVENT = 'notification-created'
const PUSHER_DOWN_CONNECTION_EVENTS = ['unavailable', 'failed', 'disconnected']

// Faster than notificationUnreadCountQuery's own 60s safety-net
// refetchInterval, so the badge doesn't lag for up to a minute while Pusher
// is known to be down, but not so fast it hammers the count/list endpoints.
const FALLBACK_POLL_INTERVAL_MS = 20_000

// Small buffer past 1 in case another notification lands in the same
// instant as the one this lookup is chasing - still cheap, and the receipt
// we actually want is actually almost always first (sorted by createdAt
// DESC) since it was just created.
const DELIVERY_LOOKUP_LIMIT = 5

export function useNotificationRealtimeUpdates({
  enabled,
  channelName,
  onNotificationDelivered,
  onNotificationReceived,
}: {
  enabled: boolean
  /**
   * notification-service's own per-user Pusher channel (from the
   * notification-count response's `pusherChannelName` - see
   * notificationUnreadCountQuery.ts). Undefined/empty until that query has
   * loaded at least once, or if Pusher isn't configured server-side; either
   * way this hook simply doesn't connect yet.
   */
  channelName: string | undefined
  /**
   * Fired once per real-time `notification-created` event, after this hook
   * attempts to resolve the new receipt via a direct, one-off list fetch
   * (bypassing the cache - see the handler below for why).
   *
   * `sourceNotificationId` is always the raw id from the Pusher payload
   * itself (notification-service's own Notification id, not a receipt id) -
   * present on every call, found or not, so a caller always has something to
   * correlate against even on a failed lookup.
   *
   * `notificationId`/`notificationType` describe the matched read receipt -
   * the same kind of id `notification_clicked` reports (a receipt id, not
   * notification-service's id) - and are `undefined` together if the lookup
   * fails, or the receipt isn't among the most recent few (e.g. several
   * notifications landed in the same instant). Deliberately never falls
   * back to `sourceNotificationId` for `notificationId`: those are two
   * different kinds of id, and conflating them in one field would make a
   * downstream join against `notification_clicked.notification_id` quietly
   * miss on exactly the rows where the fallback kicked in.
   *
   * Not called for the fallback-poll path (see `usePollUntil` below): the
   * poll has no per-notification payload to attribute a delivery event to.
   */
  onNotificationDelivered?: (
    notificationId: string | undefined,
    notificationType: string | undefined,
    sourceNotificationId: string
  ) => void
  /**
   * Fired alongside `onNotificationDelivered`, from the same lookup (it does
   * not trigger a second request) - this one hands back the full matched
   * receipt instead of just an id/type pair, for a caller that needs more of
   * it (e.g. `content.jsonPayload`/`content.deepLink` to build a candidate
   * for a different surface) rather than just reporting delivery.
   *
   * `receipt` is `undefined` exactly when `onNotificationDelivered`'s
   * `notificationId`/`notificationType` would be `undefined` (no access
   * token, lookup failure, or no match among the most recent few) -
   * `sourceNotificationId` is always present, same as the other callback.
   *
   * Not called for the fallback-poll path, for the same reason
   * `onNotificationDelivered` isn't.
   */
  onNotificationReceived?: (
    receipt: NotificationReadReceiptItemDto | undefined,
    sourceNotificationId: string
  ) => void
}) {
  const queryClient = useQueryClient()
  const [isPusherUnavailable, setIsPusherUnavailable] = useState(false)

  const personId = useDecodedAccessToken()?.personId ?? ''
  const accessToken = useAccessToken()

  // Gated on channelName too (not just `enabled`) so a connection is never
  // opened before there's anything to subscribe it to.
  const canConnect = enabled && !!channelName

  const { PUBLIC_PUSHER_APP_KEY, PUBLIC_PUSHER_APP_CLUSTER } =
    getPublicEnvVars()
  const pusherClient = usePusher(
    canConnect ? PUBLIC_PUSHER_APP_KEY : undefined,
    canConnect ? PUBLIC_PUSHER_APP_CLUSTER : undefined
  )

  const invalidateNotificationQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: getNotificationUnreadCountKey(personId),
      }),
      queryClient.invalidateQueries({
        queryKey: getNotificationListKey(personId),
      }),
    ])
  }, [queryClient, personId])

  // Read via a ref inside the connection-listener effect below rather than
  // listed as a dependency - otherwise this effect would tear down and
  // re-bind (re-running its cleanup) on every identity change of this
  // callback, which would also spuriously reset isPusherUnavailable.
  const invalidateNotificationQueriesRef = useRef(invalidateNotificationQueries)
  useEffect(() => {
    invalidateNotificationQueriesRef.current = invalidateNotificationQueries
  }, [invalidateNotificationQueries])

  // Same ref-indirection reasoning as above: read the latest callback/value
  // without listing it as a dependency of the subscribe effect below, so a
  // caller re-rendering with a new callback identity (or personId changing)
  // doesn't force an unsubscribe/resubscribe of the same channel.
  const onNotificationDeliveredRef = useRef(onNotificationDelivered)
  useEffect(() => {
    onNotificationDeliveredRef.current = onNotificationDelivered
  }, [onNotificationDelivered])

  const onNotificationReceivedRef = useRef(onNotificationReceived)
  useEffect(() => {
    onNotificationReceivedRef.current = onNotificationReceived
  }, [onNotificationReceived])

  const personIdRef = useRef(personId)
  useEffect(() => {
    personIdRef.current = personId
  }, [personId])

  const accessTokenRef = useRef(accessToken)
  useEffect(() => {
    accessTokenRef.current = accessToken
  }, [accessToken])

  useEffect(() => {
    if (!canConnect || !pusherClient) return

    // Local to this effect instance (not a ref) since onDown/onConnected are
    // only ever read/written from within this same closure - tracks whether
    // a `connected` event is a recovery (so it's worth a catch-up refetch)
    // or just the initial connect.
    let wasDown = false
    const onDown = () => {
      wasDown = true
      setIsPusherUnavailable(true)
    }
    const onConnected = () => {
      if (wasDown) {
        wasDown = false
        void invalidateNotificationQueriesRef.current()
      }
      setIsPusherUnavailable(false)
    }

    for (const event of PUSHER_DOWN_CONNECTION_EVENTS) {
      pusherClient.connection.bind(event, onDown)
    }
    pusherClient.connection.bind('connected', onConnected)

    // The shared connection may already be down by the time this effect
    // binds (e.g. another hook/tab drove it down before this one mounted) -
    // without this, the fallback poll wouldn't start until the next state
    // transition fires, which may never come if nothing else touches it.
    if (PUSHER_DOWN_CONNECTION_EVENTS.includes(pusherClient.connection.state)) {
      onDown()
    }

    return () => {
      for (const event of PUSHER_DOWN_CONNECTION_EVENTS) {
        pusherClient.connection.unbind(event, onDown)
      }
      pusherClient.connection.unbind('connected', onConnected)
      // Covers both unmount and canConnect flipping false (e.g. the channel
      // name disappearing) - without this, isPusherUnavailable could stay
      // stuck true with nothing left to ever flip it back.
      setIsPusherUnavailable(false)
    }
  }, [canConnect, pusherClient])

  useEffect(() => {
    if (!canConnect || !pusherClient || !channelName) return

    // usePusher pools one connection app-wide (see its doc comment), but
    // unlike the old account-service stand-in, this channel is
    // notification-service's own per-user channel - nothing else subscribes
    // to it, so it's safe to fully unsubscribe on cleanup (channelName
    // changing, e.g. on logout/login, or unmount) rather than leaving it
    // open indefinitely. This assumes a single owner: this hook must stay
    // the only subscriber to this channel, since Pusher subscriptions on
    // the pooled client aren't reference-counted - a second consumer
    // mounting its own instance would have its live updates silently
    // dropped whenever either instance unmounts.
    //
    // That single-owner assumption is per-tab, not per-user: a user with N
    // tabs open mounts N separate instances of this hook, each with its own
    // Pusher connection, so one real notification-created event fans out
    // into N separate notification_delivered events (and N lookup
    // requests). Not deduplicated client-side - flagged here for whoever
    // consumes this analytics event downstream, since delivery counts will
    // be inflated by open-tab count, not just notification count.
    const channel = pusherClient.subscribe(channelName)
    // Flipped to true on cleanup (unmount, or canConnect/pusherClient/
    // channelName changing - e.g. logout) so a lookup that was already in
    // flight can't call onDelivered for a session this hook no longer
    // represents.
    let isCancelled = false
    const handleNotificationCreated = async (
      // Typed loosely on purpose: nothing confirms notification-service's
      // actual Pusher payload always has this shape (see the null-check
      // below).
      pusherPayload: { notificationId?: string } | null | undefined
    ) => {
      // Fire-and-forget, matching every other call site of this ref in the
      // file (e.g. the connection-listener effect's onConnected handler) -
      // not awaited, so the delivery lookup below runs concurrently with it
      // instead of waiting on a full round trip first, and so a rejection
      // here can't also take down the lookup/callback below with it.
      void invalidateNotificationQueriesRef.current()

      const onDelivered = onNotificationDeliveredRef.current
      const onReceived = onNotificationReceivedRef.current
      if (!onDelivered && !onReceived) return

      // Defensive: nothing confirms notification-service's actual Pusher
      // payload always matches this shape (this hasn't been exercised
      // against a live connection - see the MR this shipped in). A
      // malformed/empty payload reaching `.notificationId` below would throw
      // outside any try/catch here, becoming an unhandled rejection instead
      // of the "still fire the callback" behavior every other branch in this
      // function follows.
      const sourceNotificationId = pusherPayload?.notificationId
      if (!sourceNotificationId) return

      const accessToken = accessTokenRef.current
      if (!accessToken) {
        // No token means the lookup can only fail - skip the doomed request
        // and go straight to the same fallback a failed lookup would reach.
        onDelivered?.(undefined, undefined, sourceNotificationId)
        onReceived?.(undefined, sourceNotificationId)
        return
      }

      // A plain cache read/refetch can't resolve this reliably: the list
      // query's own observer in useNotifications.tsx is only enabled while
      // the tray is open, and TanStack Query's refetchQueries skips any
      // query whose only observer is disabled regardless of `type: 'all'`
      // (see Query.isDisabled()/isActive()) - exactly the tray-closed case
      // this lookup exists for. Fetching directly, bypassing the cache
      // entirely, sidesteps that gating (same reasoning as
      // useMarkNotificationReadOnNavigate's raw fetch).
      //
      // matchedReceipt is only computed inside the try - onDelivered/
      // onReceived are each called once, after the try/catch, so a
      // synchronous throw from either (e.g. if `track` ever stopped
      // swallowing its own errors) can't be caught by the lookup's own catch
      // block and fire a second, degraded call.
      let matchedReceipt: NotificationReadReceiptItemDto | undefined
      try {
        const { data } =
          await getNotificationReadReceiptsClient().readReceiptsApiControllerFindAll(
            {
              personId: personIdRef.current,
              limit: DELIVERY_LOOKUP_LIMIT,
              // The generated client mistypes `offset` as `object` (it's
              // documented and behaves as a plain number query param) - same
              // workaround notificationListQuery.ts already uses.
              offset: 0 as unknown as object,
              sortBy: ['createdAt'],
              sortOrder: 'DESC',
            },
            buildAxiosRequestConfig({ accessToken })
          )
        // notification-service's Pusher payload carries the *Notification*
        // document's own id, not the read-receipt's id - the receipt has
        // both, under separate fields (`_id` vs `notificationId`). Matching
        // against `_id` here would never find anything in real traffic.
        matchedReceipt = data.receipts.find(
          (receipt) => receipt.notificationId === sourceNotificationId
        )
      } catch {
        // Lookup failure (network error, not found in the most recent page,
        // etc.) shouldn't silently drop the delivery event - matchedReceipt
        // stays undefined, reported as an unknown receipt below rather than
        // not at all.
      }

      if (isCancelled) return
      // notificationId/notificationType stay undefined together when no
      // receipt matched - see this callback's doc comment for why that's
      // deliberately not backfilled from sourceNotificationId.
      //
      // Each call is isolated in its own try/catch - onDelivered and
      // onReceived are independent consumers of the same lookup result, so a
      // synchronous throw from one (e.g. if `track` ever stopped swallowing
      // its own errors) must not prevent the other from running. Logged
      // (not silently swallowed) so a real bug in a consumer still leaves a
      // trace.
      try {
        onDelivered?.(
          matchedReceipt?._id,
          matchedReceipt?.content.notificationType,
          sourceNotificationId
        )
      } catch (error) {
        console.error('onNotificationDelivered callback threw', error)
      }
      try {
        onReceived?.(matchedReceipt, sourceNotificationId)
      } catch (error) {
        console.error('onNotificationReceived callback threw', error)
      }
    }

    channel.bind(NOTIFICATION_CREATED_EVENT, handleNotificationCreated)

    return () => {
      isCancelled = true
      channel.unbind(NOTIFICATION_CREATED_EVENT, handleNotificationCreated)
      pusherClient.unsubscribe(channelName)
    }
    // invalidateNotificationQueries/onNotificationDelivered/personId/
    // accessToken are all read via refs above (see the connection-listener
    // effect's identical comment) so their identity changes don't force an
    // unnecessary unsubscribe/resubscribe of the same channel.
  }, [canConnect, pusherClient, channelName])

  // Silent fallback by design: unlike an actively-watched auction countdown,
  // nobody is staring at the header bell waiting on this update, so a toast
  // here would just be noise. Polling faster while Pusher is down (and
  // reverting to the slow safety-net interval once it reconnects) keeps the
  // badge reasonably fresh without interrupting anyone.
  //
  // Known gap: this path never calls `onNotificationDelivered`. The poll
  // only re-fetches aggregate state (unread count/list), with no
  // per-notification payload to attribute a single delivery event to -
  // unlike the Pusher path above, which gets one `notification-created`
  // event per new notification. A `notification_delivered` event can't be
  // fired reliably here, so it simply isn't while Pusher is down.
  usePollUntil(
    invalidateNotificationQueries,
    () => false,
    FALLBACK_POLL_INTERVAL_MS,
    canConnect && isPusherUnavailable
  )
}
